/**
 * React Query mutation hook for POST /api/company-intelligence/brief.
 *
 * Single call that replaces the older split between:
 *   - POST /api/company-news/analyze        (batch-level analyst brief)
 *   - POST /api/company-sourcing/article-signals (per-article cards)
 *
 * Backend persona is now equity-research (financial / EV) — NOT procurement.
 * Per-article fields kept compatible with the existing `SourcingInsightCard`
 * so the render layer doesn't have to change. The category chip's preset
 * colour map only covers the legacy procurement vocabulary; new financial
 * categories (revenue/margin/growth/etc.) render with the default chip tone —
 * harmless and easy to extend later.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../lib/api';
import type {
  ArticleInsightLink,
  ArticleRiskLink,
  CompanyNewsAnalysis,
  DaysBackOption,
  KeyInsight,
  NewsArticle,
  RiskItem,
} from './types';
import type {
  SourcingAnalysisResponse,
  SourcingArticleAnalysis,
} from '../sourcing-intelligence';

// ─── Raw wire types (mirrors src/models/company_intelligence.py) ─────────────

interface RawArticle {
  title?: string;
  display_title?: string;
  url?: string;
  source?: string | null;
  domain?: string | null;
  published_date?: string | null;
  related_fy?: string | null;
  status?: 'analyzed' | 'skipped';
  skip_reason?: string | null;
  sentiment?: string | null;
  signal_strength?: string | null;
  direction_of_impact?: 'bullish' | 'bearish' | 'neutral' | null;
  magnitude?: 'high' | 'medium' | 'low' | null;
  time_horizon?: 'immediate' | 'short' | 'medium' | 'long' | null;
  impact_category?: string | null;
  headline?: string | null;
  summary?: string | null;
  date_of_event?: string | null;
  detailed_analysis?: string | null;
  recommendations?: string[];
  confidence?: number | null;
  story_topic?: string | null;
}

interface RawInsight {
  id?: number;
  insight_label?: string;
  fact?: string;
  market_interpretation?: string;
  strategic_meaning?: string;
  unknown?: string;
  implication?: string;
}

interface RawRisk {
  name?: string;
  impact_area?: string;
  severity?: string;
  likelihood?: string;
  time_horizon?: string;
  geography?: string | null;
  type?: string;
}

interface RawArticleInsightLink {
  article_title?: string;
  insight_id?: number;
}

interface RawArticleRiskLink {
  article_title?: string;
  risk_name?: string;
}

interface RawInvestmentInterpretation {
  bullish_factors?: string[];
  bearish_factors?: string[];
  final_stance?: string;
  justification?: string;
}

interface RawConfidence {
  level?: string;
  reason?: string;
}

interface RawIntelligenceBatch {
  id?: string;
  created_at?: string;
  article_count?: number;
  analyzed_count?: number;
  skipped_count?: number;
}

interface RawDirectionBreakdown {
  bullish?: number;
  bearish?: number;
  neutral?: number;
}

interface RawAnalysis {
  company?: string;
  batch?: RawIntelligenceBatch;
  analysis_mode?: string;
  articles?: RawArticle[];
  key_insights?: RawInsight[];
  risks?: RawRisk[];
  article_insight_links?: RawArticleInsightLink[];
  article_risk_links?: RawArticleRiskLink[];
  contrarian_view?: string[] | string;
  competitive_positioning?: string[];
  investment_interpretation?: RawInvestmentInterpretation;
  confidence?: RawConfidence;
  data_limitations?: string[];
  summary?: string;
  direction_breakdown?: RawDirectionBreakdown;
  avg_confidence?: number | null;
}

interface RawApiResponse {
  company_name: string;
  days_back: number;
  status: 'success' | 'no_articles' | 'partial';
  articles_fetched: number;
  articles_analyzed: number;
  articles_skipped: number;
  analysis?: RawAnalysis | null;
  llm_provider?: string | null;
  tokens_used?: number | null;
  generated_at: string;
}

// ─── Public output shape (carries BOTH views) ────────────────────────────────

export interface CompanyIntelligenceBrief {
  news: CompanyNewsAnalysis;
  sourcing: SourcingAnalysisResponse;
  status: 'success' | 'no_articles' | 'partial';
}

// ─── Normaliser ──────────────────────────────────────────────────────────────

function normalize(raw: RawApiResponse): CompanyIntelligenceBrief {
  const a = raw.analysis ?? {};
  const rawArticles = a.articles ?? [];

  // Per-company stable id maps for link translation.
  const titleToId = new Map<string, string>();
  rawArticles.forEach((art, idx) => {
    if (art.title) titleToId.set(art.title, `art_${idx}`);
  });
  const numToInsightId = new Map<number, string>();
  (a.key_insights ?? []).forEach((ins) => {
    if (ins.id != null) numToInsightId.set(ins.id, `insight_${ins.id}`);
  });
  const nameToRiskId = new Map<string, string>();
  (a.risks ?? []).forEach((r, idx) => {
    if (r.name) nameToRiskId.set(r.name, `risk_${idx}`);
  });

  // ── 1. News-shape projection (for ExecOverview / BusinessInsights / Risks / TopArticles)
  const newsArticles: NewsArticle[] = rawArticles.map((art, idx) => ({
    article_id: `art_${idx}`,
    display_title: art.display_title ?? art.title ?? 'Untitled',
    source: art.source ?? undefined,
    domain: art.domain ?? undefined,
    url: art.url ?? undefined,
    published_date: art.published_date ?? null,
    signal_strength: art.signal_strength ?? undefined,
    sentiment: art.sentiment ?? undefined,
    related_fy: art.related_fy ?? null,
    summary: art.summary ?? undefined,
  }));

  const key_insights: KeyInsight[] = (a.key_insights ?? []).map((ins) => ({
    insight_id: ins.id != null ? `insight_${ins.id}` : `insight_${Math.random()}`,
    insight_label: ins.insight_label ?? '—',
    fact: ins.fact,
    market_interpretation: ins.market_interpretation,
    strategic_meaning: ins.strategic_meaning,
    unknown: ins.unknown,
    implication: ins.implication,
    confidence: a.confidence?.level,
    contrarian: false,
    related_fy: null,
  }));

  const risks: RiskItem[] = (a.risks ?? []).map((r, idx) => ({
    risk_id: `risk_${idx}`,
    risk_name: r.name ?? '—',
    severity: r.severity,
    likelihood: r.likelihood,
    impact_area: r.impact_area,
    time_horizon: r.time_horizon,
    geography: r.geography,
    type: r.type,
    origin:
      r.type === 'inferred' ? 'inferred' : r.type === 'explicit' ? 'explicit' : undefined,
    related_fy: null,
  }));

  const article_insight_links: ArticleInsightLink[] = [];
  (a.article_insight_links ?? []).forEach((link) => {
    const article_id = link.article_title ? titleToId.get(link.article_title) : undefined;
    const insight_id = link.insight_id != null ? numToInsightId.get(link.insight_id) : undefined;
    if (article_id && insight_id) article_insight_links.push({ article_id, insight_id });
  });

  const article_risk_links: ArticleRiskLink[] = [];
  (a.article_risk_links ?? []).forEach((link) => {
    const article_id = link.article_title ? titleToId.get(link.article_title) : undefined;
    const risk_id = link.risk_name ? nameToRiskId.get(link.risk_name) : undefined;
    if (article_id && risk_id) article_risk_links.push({ article_id, risk_id });
  });

  const rawContrarian = a.contrarian_view;
  const contrarianText = Array.isArray(rawContrarian)
    ? rawContrarian.join('\n\n')
    : typeof rawContrarian === 'string'
    ? rawContrarian
    : undefined;

  const interp = a.investment_interpretation;
  const news: CompanyNewsAnalysis = {
    company_name: raw.company_name ?? a.company ?? 'Unknown',
    analysis_mode: a.analysis_mode,
    confidence: a.confidence?.level,
    news_batch: {
      batch_id: a.batch?.id,
      fetched_at: a.batch?.created_at ?? raw.generated_at,
      total_articles: a.batch?.article_count ?? newsArticles.length,
    },
    articles: newsArticles,
    key_insights,
    risks,
    article_insight_links,
    article_risk_links,
    investment_interpretation: interp
      ? {
          stance: interp.final_stance,
          summary: a.summary,
          bullish_factors: interp.bullish_factors ?? [],
          bearish_factors: interp.bearish_factors ?? [],
          justification: interp.justification,
          contrarian_view: contrarianText,
        }
      : undefined,
    data_limitations: a.data_limitations ?? [],
    related_fy: [
      ...new Set(
        newsArticles.map((art) => art.related_fy).filter((v): v is string => v != null)
      ),
    ],
  };

  // ── 2. Sourcing-shape projection (for per-article cards — keeps existing
  // SourcingInsightCard component compatible without a render-layer rewrite).
  const sourcingArticles: SourcingArticleAnalysis[] = rawArticles.map((art) => ({
    article_url: art.url ?? '',
    article_title: art.title ?? art.display_title ?? 'Untitled',
    article_published_date: art.published_date ?? null,
    article_source: art.source ?? null,

    status: art.status ?? 'analyzed',
    skip_reason: art.skip_reason ?? null,

    headline: art.headline ?? null,
    title: art.title ?? null,
    summary: art.summary ?? null,
    date_of_event: art.date_of_event ?? null,
    // The card reads `category_impact`; backend emits `impact_category` under
    // the new financial taxonomy (revenue/margin/growth/...). Tone lookup
    // falls back to undefined for the new keys — chip still renders.
    category_impact: (art.impact_category as SourcingArticleAnalysis['category_impact']) ?? null,
    direction_of_impact: art.direction_of_impact ?? null,
    magnitude: art.magnitude ?? null,
    time_horizon: art.time_horizon ?? null,
    relevance_to_sourcing: null,
    action_trigger: null,
    action_detail: null,
    detailed_analysis: art.detailed_analysis ?? null,
    recommendations: art.recommendations ?? [],
    // The card's `sentiment` chip uses bullish/bearish/neutral/cautious — same
    // shape as direction_of_impact, so we map straight through.
    sentiment:
      (art.direction_of_impact as SourcingArticleAnalysis['sentiment']) ?? null,
    impact_level: art.magnitude ?? null,
    confidence: art.confidence ?? null,
    related_commodities: [],
    related_categories: [],
    related_suppliers: [],
    matched_category_ids: [],

    tokens_used: null,
    llm_provider: raw.llm_provider ?? null,
    // Story-clustering label — articles with the same story_topic are
    // consolidated into a single multi-source card in the UI.
    story_topic: art.story_topic ?? null,
  }));

  const directionBreakdown: Record<string, number> = {
    bullish: a.direction_breakdown?.bullish ?? 0,
    bearish: a.direction_breakdown?.bearish ?? 0,
    neutral: a.direction_breakdown?.neutral ?? 0,
  };

  const analyzedCount = sourcingArticles.filter((x) => x.status === 'analyzed').length;
  const skippedCount = sourcingArticles.filter((x) => x.status === 'skipped').length;

  const sourcing: SourcingAnalysisResponse = {
    scope_id: `company:${(raw.company_name ?? '').toLowerCase().trim().replace(/\s+/g, '_')}`,
    scope_name: raw.company_name ?? '',
    company_name: raw.company_name ?? null,
    days_back: raw.days_back,
    // Sourcing UI treats "no_articles" + "all_skipped" + "success" + "partial".
    // Map the intel statuses through; an all-skipped response will still come
    // back as "success" or "partial" — that's fine, the render branches off
    // analyzed_count anyway.
    status:
      raw.status === 'no_articles'
        ? 'no_articles'
        : analyzedCount === 0 && skippedCount > 0
        ? 'all_skipped'
        : raw.status,
    articles_fetched: raw.articles_fetched,
    articles_analyzed: analyzedCount,
    articles_skipped: skippedCount,
    analysis: raw.analysis
      ? {
          scope_id: `company:${(raw.company_name ?? '').toLowerCase().trim().replace(/\s+/g, '_')}`,
          scope_name: raw.company_name ?? '',
          persona: 'Senior equity-research analyst',
          batch: {
            id: a.batch?.id ?? '',
            scope_id: '',
            created_at: a.batch?.created_at ?? raw.generated_at,
            article_count: a.batch?.article_count ?? sourcingArticles.length,
            analyzed_count: a.batch?.analyzed_count ?? analyzedCount,
            skipped_count: a.batch?.skipped_count ?? skippedCount,
          },
          articles: sourcingArticles,
          total_articles: sourcingArticles.length,
          analyzed_count: analyzedCount,
          skipped_count: skippedCount,
          actions_by_trigger: {},
          direction_breakdown: directionBreakdown,
          avg_confidence: a.avg_confidence ?? null,
        }
      : null,
    llm_provider: raw.llm_provider ?? null,
    tokens_used: raw.tokens_used ?? null,
    generated_at: raw.generated_at,
  };

  return { news, sourcing, status: raw.status };
}

// ─── Cache + API call ────────────────────────────────────────────────────────

const QUERY_KEY = ['company-intelligence', 'brief'] as const;
const STALE_TIME_MS = 1000 * 60 * 60 * 12; // 12 h — matches backend Neo4j freshness window

interface CacheEntry {
  data: CompanyIntelligenceBrief;
  fetchedAt: number;
}

const cacheKey = (companyName: string, daysBack: number) =>
  `${companyName.trim().toLowerCase()}::${daysBack}`;

interface BriefRequest {
  company_name: string;
  days_back: number;
  max_articles?: number;
}

/**
 * Best-effort invalidation of the backend Neo4j cache for this company.
 * Called before a "hard refresh" so the next POST /brief re-runs the full
 * synthesis pipeline instead of returning the 12h-old cached payload.
 *
 * Failures (network, 404, etc.) are intentionally swallowed: if cache clear
 * fails the worst case is the user gets the stale payload again, which is
 * the current behavior anyway. We don't want a transient DELETE error to
 * block the refresh path.
 */
async function clearBackendCache(companyName: string): Promise<void> {
  try {
    await apiClient.delete('/company-intelligence/cache', {
      params: { company_name: companyName },
      // Short timeout — the DELETE is a quick Neo4j write, no LLM in the
      // path. If it hangs longer than this, something is wrong on the
      // server and we should proceed to re-fetch anyway.
      timeout: 10_000,
    });
  } catch {
    // intentional: see jsdoc
  }
}

async function postBrief(
  req: BriefRequest,
  signal?: AbortSignal,
): Promise<CompanyIntelligenceBrief> {
  const { data } = await apiClient.post<RawApiResponse>(
    '/company-intelligence/brief',
    req,
    // 5 min — uncapped article runs (typically 30-90 articles) can take
    // 2-4 min: ~70s body enrichment at concurrency=12 + ~30-60s LLM call.
    // The 12h Neo4j cache absorbs the cost after the first hit per day.
    // `signal` lets the hook cancel an in-flight request when the user
    // switches days_back before the previous request finished — otherwise
    // two /brief calls run in parallel and compete for Neo4j connections.
    { timeout: 300_000, signal },
  );
  return normalize(data);
}

// ─── Public hook types ───────────────────────────────────────────────────────

export interface UseCompanyIntelligenceBriefOptions {
  companyName: string | null | undefined;
  defaultDaysBack?: DaysBackOption;
  defaultMaxArticles?: number;
  autoFetch?: boolean;
}

export interface UseCompanyIntelligenceBriefResult {
  data: CompanyIntelligenceBrief | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  daysBack: DaysBackOption;
  setDaysBack: (value: DaysBackOption) => void;
  refresh: () => void;
  fetchedAt: number | null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useCompanyIntelligenceBrief({
  companyName,
  defaultDaysBack = 90,
  defaultMaxArticles,
  autoFetch = true,
}: UseCompanyIntelligenceBriefOptions): UseCompanyIntelligenceBriefResult {
  const queryClient = useQueryClient();
  const [daysBack, setDaysBack] = useState<DaysBackOption>(defaultDaysBack);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const inflightKeyRef = useRef<string | null>(null);
  // Tracks the AbortController for the request currently in flight, so a
  // window switch (90 → 7) can cancel the running request instead of
  // letting both calls race in parallel and compete for Neo4j connections.
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation<
    CompanyIntelligenceBrief,
    Error,
    BriefRequest & { __signal?: AbortSignal }
  >({
    mutationFn: ({ __signal, ...req }) => postBrief(req, __signal),
    onSettled: () => {
      inflightKeyRef.current = null;
      abortRef.current = null;
    },
    onSuccess: (normalized, variables) => {
      const now = Date.now();
      setFetchedAt(now);
      queryClient.setQueryData<CacheEntry>(
        [...QUERY_KEY, cacheKey(variables.company_name, variables.days_back)],
        { data: normalized, fetchedAt: now }
      );
    },
  });

  const trigger = useCallback(
    async (force = false, hardRefresh = false) => {
      if (!companyName) return;
      const reqKey = cacheKey(companyName, daysBack);
      if (!force && inflightKeyRef.current === reqKey) return;
      const key = [...QUERY_KEY, reqKey];
      const cached = queryClient.getQueryData<CacheEntry>(key);
      if (!force && cached && Date.now() - cached.fetchedAt < STALE_TIME_MS) {
        setFetchedAt(cached.fetchedAt);
        return;
      }
      // Hard refresh: wipe the backend Neo4j payload cache BEFORE issuing
      // the POST /brief, so synthesis re-runs from scratch instead of
      // returning the 12h-old response. The React Query cache for this
      // (company, daysBack) is also dropped so the mutation result becomes
      // the source of truth, not the stale entry.
      if (hardRefresh) {
        queryClient.removeQueries({ queryKey: key });
        await clearBackendCache(companyName);
      }
      // Cancel any prior in-flight request before firing a new one. This
      // prevents the two-parallel-/brief-calls scenario: when the user
      // switches days_back (or the hook re-fires on a re-render) while a
      // previous /brief is still running, the previous request is aborted
      // and only the latest one continues. Without this, both ran to
      // completion and competed for Neo4j connections on the backend.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      inflightKeyRef.current = reqKey;
      const body: BriefRequest & { __signal?: AbortSignal } = {
        company_name: companyName,
        days_back: daysBack,
        __signal: controller.signal,
      };
      if (defaultMaxArticles !== undefined) {
        body.max_articles = defaultMaxArticles;
      }
      mutation.mutate(body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyName, daysBack, queryClient]
  );

  useEffect(() => {
    if (!autoFetch || !companyName) return;
    const id = setTimeout(() => {
      void trigger(false);
    }, 0);
    return () => {
      clearTimeout(id);
      // If the effect tears down (unmount, dependency change) while a
      // request is in flight, cancel it. The hook's caller doesn't need
      // the result anymore.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [autoFetch, companyName, daysBack, trigger]);

  const cached = companyName
    ? queryClient.getQueryData<CacheEntry>([
        ...QUERY_KEY,
        cacheKey(companyName, daysBack),
      ])
    : undefined;

  const data = cached?.data ?? mutation.data ?? null;

  return {
    data,
    isLoading: mutation.isPending && !data,
    isRefreshing: mutation.isPending && !!data,
    error: mutation.error ? mutation.error.message : null,
    daysBack,
    setDaysBack,
    refresh: () => {
      // Hard refresh: invalidate backend Neo4j payload cache, then re-fetch.
      // Without this, hitting the refresh button just re-served the same
      // cached payload from Neo4j until the 12h TTL expired.
      void trigger(true, true);
    },
    fetchedAt: cached?.fetchedAt ?? fetchedAt,
  };
}
